import type { ReactNode } from 'react'

import { useTabOwnerId } from '@/components/layout/SheetContainerContext'
import { useWorkspaceCloseOwner } from '@/hooks/useWorkspaceCloseOwner'
import { useEditorRegistry } from '@/stores/editorRegistry'

/**
 * Bridges a composite route — a duty, an eval case: ~10 editors edited
 * together — to the tab shell, which holds exactly ONE close owner per tab.
 * The owner saves every dirty editor registered under this tab id.
 *
 * Mounted by the route factory ABOVE the sub-view swap, so a card in any
 * sub-view registers into the same tab. Renders its children unchanged, and
 * stays a single-child wrapper because mainRoutes' guard-chain walker follows
 * the element tree through it.
 *
 * The dirty MARKER is not published here: a card's dirtiness is the card's own
 * state, so its edge is published by `useRegisterTabEditor`, and membership
 * changes by the registry's register/unregister.
 */
export function CompositeTabScope({ children }: { children: ReactNode }) {
  const tabId = useTabOwnerId() ?? ''
  useWorkspaceCloseOwner({
    save: () => useEditorRegistry.getState().saveAllDirty(tabId),
  })
  return <>{children}</>
}
